import type {
  PopStoreSubscriptionLevel,
  PopStoreUserSubscriptionInfo,
} from '@ps/models/lib/creator-subscription';
import { isAfter } from 'date-fns';

import type { CheckUserSubscriptionStatusData } from './checkUserSubscriptionStatus';

/**
 * The signed-in user's subscription standing with one creator, mirroring
 * PopshopRN's `UserSubscriptionState`:
 *
 * - `accessLevel` — highest tier the user can currently access. A canceled-but-
 *   still-valid subscription counts (access runs until the paid term ends).
 * - `activeSubscriptionLevel` — the tier the user is *actively* paying for;
 *   `undefined` once canceled. Use it to tell "Subscribe" from "Upgrade".
 * - `canceled` — subscribed but since canceled (or refunded); `undefined` if they
 *   never subscribed or the subscription already expired.
 * - `queuedSubscription` — a future-dated subscription change, if any.
 */
export interface CurrentUserSubscriptionState {
  accessLevel: PopStoreSubscriptionLevel | undefined;
  activeSubscriptionLevel: PopStoreSubscriptionLevel | undefined;
  canceled: boolean | undefined;
  queuedSubscription: PopStoreUserSubscriptionInfo | null | undefined;
}

const EMPTY_STATE: CurrentUserSubscriptionState = {
  accessLevel: undefined,
  activeSubscriptionLevel: undefined,
  canceled: undefined,
  queuedSubscription: undefined,
};

/**
 * `subscription_end_date` is a unix timestamp — seconds, or milliseconds when it
 * carries 13+ digits. Returns `null` for a missing/zero value. Mirrors PopshopRN's
 * `timestampFromDate` numeric handling.
 */
function endDateToDate(endDate: number | null | undefined): Date | null {
  if (!endDate) return null;
  const millis = endDate.toString().length >= 13 ? endDate : endDate * 1000;
  return new Date(millis);
}

/**
 * Derive the user's subscription standing from the raw `checkUserSubscriptionStatus`
 * response. Pure (no React / network) so both the engine init and any UI hook can
 * share it.
 */
export function deriveSubscriptionState(
  data: CheckUserSubscriptionStatusData | null,
): CurrentUserSubscriptionState {
  const topLevelSubscription = data?.top_level_subscription;
  if (topLevelSubscription == null) return EMPTY_STATE;

  const endDate = endDateToDate(topLevelSubscription.subscription_end_date);
  // A subscription valid past now grants access — even once canceled, the paid
  // term still runs out in the future.
  const accessLevel =
    endDate != null && isAfter(endDate, new Date())
      ? topLevelSubscription.platform_sub_level
      : undefined;
  const canceled =
    topLevelSubscription.subscription_status === 'cancelled' ||
    topLevelSubscription.subscription_status === 'refunded';
  // Actively paying = still has access AND hasn't canceled.
  const activeSubscriptionLevel =
    accessLevel != null && !canceled ? accessLevel : undefined;

  return {
    accessLevel,
    activeSubscriptionLevel,
    canceled,
    queuedSubscription: data?.queued_subscription ?? null,
  };
}
